/**
 * EventTimeline — a chronological audit trail (newest-first) for an event OR an
 * order. Each entry shows its icon/action, who did it, an absolute + relative time,
 * and — for edits — a color-coded, before→after list of exactly what changed
 * (parsed from the stored diff `summary` via splitChanges). Pass `placedAt` to
 * append a synthetic "Order placed" entry (order timelines derive placement from
 * submittedAt rather than storing it).
 */
import React from 'react';
import { HISTORY_META, timeAgo, formatDateTime, toMs, splitChanges } from '../eventUtils';
import { t } from '../strings';

/** One parsed change token → a color-coded line (add = green, remove = red, change = before→after). */
function Change({ c }) {
  if (c.kind === 'add') {
    return (
      <li className="ev-chg ev-chg--add">
        <span className="ev-chg__ico">＋</span>
        <span>{c.label ? <><b>{c.label}</b> <ins>{c.to}</ins></> : c.text}</span>
      </li>
    );
  }
  if (c.kind === 'remove') {
    return (
      <li className="ev-chg ev-chg--remove">
        <span className="ev-chg__ico">－</span>
        <span>{c.label ? <b>{c.label}</b> : <del>{c.text}</del>}</span>
      </li>
    );
  }
  if (c.kind === 'change') {
    return (
      <li className="ev-chg ev-chg--change">
        <span>
          {c.label ? <b>{c.label} </b> : null}
          <del>{c.from}</del> <span className="ev-chg__arr">→</span> <ins>{c.to}</ins>
        </span>
      </li>
    );
  }
  return <li className="ev-chg ev-chg--note"><span>{c.text}</span></li>;
}

export default function EventTimeline({ history, placedAt }) {
  const items = [...(history || [])];
  if (placedAt) items.push({ action: 'placed', at: toMs(placedAt) });
  items.sort((a, b) => (b.at || 0) - (a.at || 0));

  if (!items.length) return <p className="ev-empty-sm">{t('noActivity')}</p>;

  return (
    <div className="ev-tl">
      {items.map((h, i) => {
        const meta = HISTORY_META[h.action] || { icon: '•', label: h.action };
        const changes = splitChanges(h.summary);
        // Show the raw summary only if it's non-empty and we couldn't parse it (old entries).
        const rawSummary = changes.length === 0 && h.summary && h.summary !== 'updated' ? h.summary : null;
        return (
          <div key={`${h.at}-${i}`} className="ev-tl__row">
            <span className="ev-tl__dot">{meta.icon}</span>
            <div className="ev-tl__body">
              <div className="ev-tl__head">
                <span className="ev-tl__action">
                  {meta.label}{h.by ? <span className="ev-tl__by"> · {h.by}</span> : null}
                </span>
                <span className="ev-tl__time">
                  {formatDateTime(h.at)}{timeAgo(h.at) ? <span className="ev-tl__ago"> · {timeAgo(h.at)}</span> : null}
                </span>
              </div>
              {changes.length > 0 && (
                <ul className="ev-tl__changes">
                  {changes.map((c, ci) => <Change key={ci} c={c} />)}
                </ul>
              )}
              {rawSummary ? <span className="ev-tl__summary">{rawSummary}</span> : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
